<template>
    <span class="ipo" @mouseenter="onEnter" @mouseleave="onLeave">
        <button type="button" ref="trigger" class="ipo-trigger" :class="{'is-open': open, 'is-custom': !!$slots.trigger}"
            :aria-label="label || `Ajuda: ${title}`" :aria-expanded="String(open)" :aria-describedby="open ? id : null"
            @click.stop.prevent="togglePin" @focus="show" @blur="onBlur" @keydown.esc="close">
            <!-- Sem slot, o gatilho é o "?"; com slot, o próprio conteúdo (ex.: uma barra de progresso) -->
            <slot name="trigger">?</slot>
        </button>

        <div ref="pop" :id="id" role="tooltip" class="ipo-pop" :class="[`is-${placement}`, {'is-open': open}]"
            :style="popStyle" @mouseenter="onEnter" @mouseleave="onLeave">
            <span class="ipo-arrow" :style="arrowStyle"></span>
            <strong class="ipo-title">{{title}}</strong>
            <div class="ipo-body"><slot /></div>
        </div>
    </span>
</template>

<script>
// Popover de ajuda ("?") no padrão da edição do produto. Abre no hover, no foco
// e no clique (que fixa aberto); fecha com Esc ou clique fora. O balão vai para
// o <body> para não ser cortado por overflow das grades e tabelas.
let uid = 0

const GAP = 10
const MARGIN = 12
const CLOSE_DELAY = 140

export default {
    name: 'InfoPopoverCmp',
    props: {
        title: {
            type: String,
            default: 'O que é isso?',
        },
        width: {
            type: Number,
            default: 280,
        },
        // Nome acessível do gatilho, quando ele não é o "?"
        label: {
            type: String,
            default: '',
        },
    },
    data() {
        uid += 1

        return {
            id: `ipo-${uid}`,
            open: false,
            pinned: false,
            placement: 'right',
            top: 0,
            left: 0,
            arrowOffset: 16,
            closeTimer: null,
        }
    },
    computed: {
        popStyle() {
            return {top: `${this.top}px`, left: `${this.left}px`, width: `${this.width}px`}
        },
        arrowStyle() {
            const vertical = this.placement === 'right' || this.placement === 'left'

            return vertical ? {top: `${this.arrowOffset}px`} : {left: `${this.arrowOffset}px`}
        },
    },
    mounted() {
        document.body.appendChild(this.$refs.pop)
    },
    beforeDestroy() {
        this.unbind()
        clearTimeout(this.closeTimer)

        const pop = this.$refs.pop

        if (pop && pop.parentNode) {
            pop.parentNode.removeChild(pop)
        }
    },
    methods: {
        show() {
            clearTimeout(this.closeTimer)

            if (this.open) {
                return
            }

            this.open = true
            this.bind()
            this.$nextTick(this.position)
        },
        close() {
            clearTimeout(this.closeTimer)
            this.open = false
            this.pinned = false
            this.unbind()
        },
        togglePin() {
            if (this.pinned) {
                this.close()
                return
            }

            this.pinned = true
            this.show()
        },
        onEnter() {
            this.show()
        },
        onLeave() {
            if (this.pinned) {
                return
            }

            clearTimeout(this.closeTimer)
            this.closeTimer = setTimeout(this.close, CLOSE_DELAY)
        },
        onBlur() {
            if (!this.pinned) {
                this.onLeave()
            }
        },
        onDocumentClick(e) {
            if (!this.$el.contains(e.target) && !this.$refs.pop.contains(e.target)) {
                this.close()
            }
        },
        onKeydown(e) {
            if (e.key === 'Escape') {
                this.close()
            }
        },
        bind() {
            document.addEventListener('click', this.onDocumentClick, true)
            document.addEventListener('keydown', this.onKeydown)
            window.addEventListener('scroll', this.position, true)
            window.addEventListener('resize', this.position)
        },
        unbind() {
            document.removeEventListener('click', this.onDocumentClick, true)
            document.removeEventListener('keydown', this.onKeydown)
            window.removeEventListener('scroll', this.position, true)
            window.removeEventListener('resize', this.position)
        },
        // Prefere à direita do "?"; se não couber, esquerda, depois baixo, depois cima
        position() {
            const trigger = this.$refs.trigger
            const pop = this.$refs.pop

            if (!this.open || !trigger || !pop) {
                return
            }

            const t = trigger.getBoundingClientRect()
            const w = pop.offsetWidth
            const h = pop.offsetHeight
            const vw = window.innerWidth
            const vh = window.innerHeight
            const cx = t.left + t.width / 2
            const cy = t.top + t.height / 2
            const clamp = (v, min, max) => Math.min(Math.max(v, min), Math.max(min, max))

            let placement = 'right'

            if (t.right + GAP + w > vw - MARGIN) {
                placement = t.left - GAP - w >= MARGIN ? 'left' : (t.bottom + GAP + h <= vh - MARGIN || t.top - GAP - h < MARGIN ? 'bottom' : 'top')
            }

            let top
            let left

            if (placement === 'right' || placement === 'left') {
                left = placement === 'right' ? t.right + GAP : t.left - GAP - w
                top = clamp(cy - 18, MARGIN, vh - MARGIN - h)
                this.arrowOffset = clamp(cy - top - 5, 10, h - 20)
            } else {
                left = clamp(cx - w / 2, MARGIN, vw - MARGIN - w)
                top = placement === 'bottom' ? t.bottom + GAP : t.top - GAP - h
                this.arrowOffset = clamp(cx - left - 5, 12, w - 22)
            }

            this.placement = placement
            this.top = Math.round(top)
            this.left = Math.round(left)
        },
    },
}
</script>

<style lang="scss" scoped>
.ipo {
    display: inline-flex;
    vertical-align: middle;
}

.ipo-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    color: var(--text-on-brand);
    background: var(--text-faint);
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0;
    text-transform: none;
    cursor: help;
    user-select: none;
    transition: background-color .15s, transform .15s;

    &:hover,
    &.is-open {
        background: var(--text-soft);
    }

    &:focus-visible {
        outline: 2px solid var(--brand-soft);
        outline-offset: 2px;
    }

    // Gatilho com conteúdo próprio: sem o círculo do "?"
    &.is-custom {
        width: auto;
        height: auto;
        padding: 4px 2px;
        border-radius: 4px;
        color: inherit;
        background: transparent;
        cursor: help;

        &:hover,
        &.is-open {
            background: transparent;
        }
    }
}

.ipo-pop {
    position: fixed;
    z-index: 1060;
    padding: 11px 13px 12px;
    border-radius: 8px;
    color: var(--text-on-tooltip-soft);
    background: var(--surface-tooltip);
    box-shadow: 0 10px 30px -8px color-mix(in srgb, var(--shadow-ink) 35%, transparent);
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    letter-spacing: 0;
    text-align: left;
    text-transform: none;
    white-space: normal;
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    transition: opacity .14s ease, transform .14s ease, visibility 0s linear .14s;

    &.is-right { transform: translateX(-4px); }
    &.is-left { transform: translateX(4px); }
    &.is-bottom { transform: translateY(-4px); }
    &.is-top { transform: translateY(4px); }

    &.is-open {
        visibility: visible;
        opacity: 1;
        pointer-events: auto;
        transform: none;
        transition: opacity .14s ease, transform .14s ease;
    }

    ::v-deep p {
        margin: 6px 0 0;
    }

    ::v-deep strong {
        color: var(--text-on-brand);
    }

    ::v-deep em {
        color: var(--danger-on-dark);
        font-style: normal;
        font-weight: 600;
    }
}

.ipo-title {
    display: block;
    color: var(--text-on-brand);
    font-weight: 700;
}

.ipo-arrow {
    position: absolute;
    width: 10px;
    height: 10px;
    background: var(--surface-tooltip);
    transform: rotate(45deg);

    .is-right > & { left: -4px; }
    .is-left > & { right: -4px; }
    .is-bottom > & { top: -4px; }
    .is-top > & { bottom: -4px; }
}

@media (prefers-reduced-motion: reduce) {
    .ipo-pop,
    .ipo-pop.is-open {
        transition: none;
    }
}
</style>
